import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  X, Wind, Cloud, Waves, Heart, Moon, Feather, Loader2,
} from 'lucide-react';
import { useAudio } from '../context/AudioContext';
import { trackEvent } from '../utils/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * "I Need a Moment" — a calming reset modal on the Today screen.
 * Six one-tap routes that now hand off to REAL published content picked
 * by the deterministic recommendation engine at /api/today/i-need-a-moment.
 * If the personalized fetch returns nothing (still empty library for that
 * bucket), we fall back to a safe route so the button is never dead.
 */

const OPTIONS = [
  { id: 'breathe',     backendKey: 'breathe',       label: 'Help Me Breathe',        sub: '60-second box breath',    icon: Wind,    accent: '#4a7c65', fallbackTo: '/breathing' },
  { id: 'calm',        backendKey: 'calm_mind',     label: 'Calm My Mind',           sub: 'A short anxiety reset',   icon: Cloud,   accent: '#7a9b8e', fallbackTo: '/category/anxiety' },
  { id: 'overwhelmed', backendKey: 'overwhelmed',   label: "I'm Overwhelmed",        sub: 'A quick reset',           icon: Waves,   accent: '#a8bfae', fallbackTo: '/workplace-reset' },
  { id: 'encourage',   backendKey: 'encouragement', label: 'I Need Encouragement',   sub: 'A word from Rowdy',       icon: Heart,   accent: '#D6AD60', fallbackTo: '/manifest' },
  { id: 'sleep',       backendKey: 'sleep',         label: 'Help Me Sleep',          sub: 'A restful drift-off',     icon: Moon,    accent: '#1e3a5f', fallbackTo: '/category/sleep' },
  { id: 'letgo',       backendKey: 'let_go',        label: 'Help Me Let Go',         sub: 'Release what is heavy',   icon: Feather, accent: '#8a7e60', fallbackTo: '/healing' },
];

const NeedAMomentModal = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { play } = useAudio();
  const [busyKey, setBusyKey] = useState(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  const routeToItem = (item, fallbackTo) => {
    if (!item) {
      navigate(fallbackTo);
      return;
    }
    const src = item.audioUrl || (item.audioAssetId ? `${API_URL}/api/media/${item.audioAssetId}` : null);
    const hasAudio = !!src;
    if (hasAudio) {
      // Start playing through the central AudioContext — same surface used
      // everywhere else in the app.
      play({
        src,
        title: item.title,
        subtitle: item.creator || 'Rowdy Solomon',
        artwork: item.coverImageUrl,
        kind: item.type === 'soundscape' ? 'soundscape' : 'audio',
        contentId: item.id,
        sourceContext: 'i_need_a_moment',
        loop: item.type === 'soundscape',
      });
      onClose();
      return;
    }
    // No direct audio → route to the item's dedicated surface if we know one,
    // otherwise the fallback category.
    onClose();
    if (item.type === 'affirmation') navigate('/manifest');
    else if (item.type === 'video') navigate('/videos');
    else navigate(`/post/${item.slug || item.id}`);
  };

  const pick = async (opt) => {
    if (busyKey) return;
    setBusyKey(opt.id);
    trackEvent('i_need_a_moment_selection', { sourceContext: opt.backendKey });
    const token = localStorage.getItem('rbr_token');
    try {
      const tz = -new Date().getTimezoneOffset();
      const r = await fetch(`${API_URL}/api/today/i-need-a-moment/${opt.backendKey}?tz_offset=${tz}&limit=1`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      let item = null;
      if (r.ok) {
        const data = await r.json();
        item = (data.items || [])[0] || null;
      }
      routeToItem(item, opt.fallbackTo);
    } catch {
      onClose();
      setTimeout(() => navigate(opt.fallbackTo), 60);
    } finally {
      setBusyKey(null);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[85] flex items-end sm:items-center justify-center bg-black/55 backdrop-blur-md animate-fade-in"
      onClick={onClose}
      data-testid="need-a-moment-overlay"
    >
      <div
        className="w-full max-w-[480px] bg-[#FFFCF3] rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden animate-slide-up max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="need-a-moment-modal"
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-stone-200/60 flex-shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#D6AD60]">
                A gentle pause
              </p>
              <h2
                className="text-2xl leading-tight mt-1 text-stone-800"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                What do you need right now?
              </h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-9 h-9 rounded-full bg-[#E8DDCB] hover:bg-[#DBC9A3] flex items-center justify-center transition-colors flex-shrink-0"
              data-testid="need-a-moment-close"
              aria-label="Close"
            >
              <X className="w-4 h-4 text-stone-700" />
            </button>
          </div>
          <p className="text-[13px] text-stone-500 mt-2 leading-relaxed">
            Take one thing off your shoulders. Pick the closest, then breathe.
          </p>
        </div>

        {/* Options */}
        <div className="px-4 py-4 overflow-y-auto flex-1">
          <div className="grid grid-cols-1 gap-2.5">
            {OPTIONS.map((o) => {
              const Icon = o.icon;
              const isBusy = busyKey === o.id;
              return (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => pick(o)}
                  disabled={!!busyKey}
                  data-testid={`need-a-moment-${o.id}`}
                  className="group flex items-center gap-4 p-4 rounded-2xl bg-[#F5EFE0] hover:bg-[#EEE4CC] active:scale-[0.98] transition-all text-left border border-transparent hover:border-[#D6AD60]/30 disabled:opacity-70"
                  style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}
                >
                  <div
                    className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-sm"
                    style={{
                      background: `linear-gradient(135deg, ${o.accent} 0%, ${o.accent}cc 100%)`,
                      color: '#FFFCF3',
                    }}
                  >
                    {isBusy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Icon className="w-5 h-5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p
                      className="text-[15px] font-semibold text-stone-800 leading-snug"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                      {o.label}
                    </p>
                    <p className="text-[12px] text-stone-500 mt-0.5">{o.sub}</p>
                  </div>
                  <div className="text-stone-400 group-hover:text-[#D6AD60] transition-colors">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <p
          className="rbr-serif-italic text-center text-stone-500 text-[12px] px-8 pb-6 pt-2 leading-relaxed flex-shrink-0"
        >
          You don&rsquo;t have to fix everything right now.
          Take a moment. Breathe. Reflect. Begin again.
        </p>
      </div>
    </div>,
    document.body
  );
};

export default NeedAMomentModal;
